/* iter-p2-n — Chapter-1 Reader Magnet public landing.
 *
 * Route: /read-free/:slug — public, no auth required.
 * Turns discovery scans into future buyers on the author's list by
 * gating the sample chapter behind an email opt-in.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, BookOpen, ShoppingBag, Mail, ArrowRight } from 'lucide-react';
import ReaderFontBar from '../components/panels/ReaderFontBar';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function ReadFreeSamplePage() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [sampleHtml, setSampleHtml] = useState('');
  const [publishingCss, setPublishingCss] = useState('');
  const [resolvedCss, setResolvedCss] = useState('');
  const [approvals, setApprovals] = useState([]);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/optional`, { withCredentials: true })
      .then((r) => setSignedIn(Boolean(r.data?.authenticated)))
      .catch(() => setSignedIn(false));
  }, []);

  useEffect(() => {
    axios.get(`${API}/reader-magnet/${slug}`)
      .then((r) => {
        setMeta(r.data);
        setPublishingCss(r.data?.publishing_css || '');
        setApprovals(r.data?.approvals || []);
        // Inject Microsoft Clarity if the author has configured it.
        // Guard: only accept the real Clarity ID format (alphanumeric / _ / -,
        // 1-30 chars) — this prevents any stored-XSS payload that slipped
        // past the backend from executing inside the <script> tag.
        const cid = r.data?.clarity_project_id;
        const _CLARITY_RE = /^[a-zA-Z0-9_-]{1,30}$/;
        if (cid && _CLARITY_RE.test(cid) && !document.getElementById('ms-clarity-script')) {
          const s = document.createElement('script');
          s.id = 'ms-clarity-script';
          s.type = 'text/javascript';
          // Use s.text (not innerHTML) so HTML-entity parsing cannot be abused.
          s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${cid}");`;
          document.head.appendChild(s);
        }
      })
      .catch((e) => setErr(e?.response?.data?.detail
                              || 'This book doesn\u2019t have a free sample right now.'));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/reader-magnet/opt-in/${slug}`, {
        email: email.trim(), name: name.trim() || null, marketing_consent: true,
      });
      setSampleHtml(r.data.sample_html || '');
      if (r.data?.publishing_css) setPublishingCss(r.data.publishing_css);
      toast.success('Enjoy the sample! We just emailed you a copy too.');
      window.setTimeout(() => {
        document.querySelector('[data-testid="rf-sample-body"]')?.scrollIntoView({ behavior: 'smooth' });
      }, 60);
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="mx-auto max-w-md p-8 text-center" data-testid="rf-error">
        <div className="text-[16px] font-semibold">Sample unavailable</div>
        <p className="mt-1 text-[13px] text-muted-foreground">{err}</p>
        <Link to="/bookstore" className="mt-3 inline-block text-[12px] text-primary underline">
          Browse the store
        </Link>
      </div>
    );
  }
  if (!meta) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="rf-page">
      <div className="grid gap-6 sm:grid-cols-[160px_minmax(0,1fr)]">
        <div>
          {meta.cover_url ? (
            <img src={meta.cover_url} alt="Cover"
                 className="w-full rounded-md border border-border shadow-md"
                 data-testid="rf-cover" />
          ) : (
            <div className="flex h-56 w-full items-center justify-center rounded-md border border-border bg-panel-header/40">
              <BookOpen className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-primary">Free chapter</div>
          <h1 className="mt-1 text-[30px] leading-tight"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              data-testid="rf-title">
            {meta.title}
          </h1>
          <div className="mt-1 text-[13px] text-muted-foreground">by {meta.author || 'Unknown Author'}</div>
          {meta.tagline && (
            <p className="mt-3 text-[13.5px] italic text-foreground/85" data-testid="rf-tagline">
              &ldquo;{meta.tagline}&rdquo;
            </p>
          )}
        </div>
      </div>

      {!sampleHtml && (
        <form onSubmit={submit}
              className="mt-8 rounded-md border border-border bg-card p-5"
              data-testid="rf-optin-form">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-primary">
            <Mail className="h-3 w-3" /> Read chapter 1 free
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Drop your email and we&rsquo;ll send you the sample chapter of{' '}
            <b className="text-foreground">{meta.title}</b>. Loved it? Grab the full book for{' '}
            <b className="text-foreground">${(meta.price_cents / 100).toFixed(2)}</b>.
          </p>
          <div className="mt-4 space-y-2">
            <Input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="h-9 text-[13px]" data-testid="rf-name" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)}
                    type="email" required
                    placeholder="you@example.com"
                    className="h-9 text-[13px]" data-testid="rf-email" />
            <Button type="submit" disabled={busy || !email}
                    className="mt-2 h-10 w-full gap-1.5 text-[13px]"
                    data-testid="rf-submit">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5" />}
              Start reading
            </Button>
          </div>
          <p className="mt-3 text-[10.5px] text-muted-foreground">
            One email. No spam. Unsubscribe anytime with a single click at the bottom of every email.
          </p>
        </form>
      )}

      {sampleHtml && (
        <>
          {(resolvedCss || publishingCss) && (
            <style dangerouslySetInnerHTML={{ __html: resolvedCss || publishingCss }} />
          )}
          <ReaderFontBar defaultCss={publishingCss} signedIn={signedIn}
                          onCssChange={setResolvedCss} />
          {approvals?.length > 0 && (
            <div className="mt-6 flex flex-wrap justify-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
                 data-testid="rf-approvals">
              {approvals.map((a) => (
                <span key={a.id || a.reviewer_kind}
                      className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                  {a.reviewer_kind} Approved · {(a.decided_at || '').slice(0, 10)}
                </span>
              ))}
            </div>
          )}
          <div className="mt-8 pub-body rounded-md border border-border bg-card p-6"
               data-testid="rf-sample-body"
               dangerouslySetInnerHTML={{
                 __html: `<section class="pub-chapter"><h2 class="pub-chapter-title">Chapter 1</h2>${sampleHtml}</section>`,
               }} />
          <div className="mt-6 rounded-md border border-primary/40 bg-primary/5 p-4"
               data-testid="rf-buy-cta">
            <div className="flex items-start gap-3">
              <ShoppingBag className="mt-0.5 h-5 w-5 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold">Loved chapter 1? Keep going.</div>
                <p className="mt-0.5 text-[12px] text-muted-foreground">
                  {meta.title} is ${(meta.price_cents / 100).toFixed(2)} — instant EPUB, PDF, and Kindle delivery.
                </p>
              </div>
              <Link to={meta.buy_url}>
                <Button size="sm" className="h-9 gap-1.5 text-[12px]"
                        data-testid="rf-buy-btn">
                  Buy the book <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
